import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Single-select with type-ahead search and an optional free-text ("Other") fallback.
 * Props:
 *  - options:  [{ value, label }]
 *  - value:    string
 *  - onChange: (next: string) => void
 *  - placeholder, emptyText, testid, disabled, allowCustom (default false), clearable (default true)
 */
export function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  emptyText = "No matches",
  testid = "searchable-select",
  disabled = false,
  allowCustom = false,
  clearable = true,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const opt = options.find((o) => o.value === value);
    return opt ? opt.label : value; // custom value renders as-is
  }, [options, value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  const exactMatch = useMemo(
    () => options.some((o) => (o.label || "").toLowerCase() === query.trim().toLowerCase()),
    [options, query]
  );

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full h-10 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
            "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("truncate text-left", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel || placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {clearable && value && !disabled && (
              <span
                role="button"
                tabIndex={0}
                data-testid={`${testid}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(""); } }}
                className="hover:text-red-600 rounded p-0.5 cursor-pointer text-muted-foreground"
                aria-label="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <div className="p-2 border-b border-slate-100 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            type="text"
            autoFocus
            placeholder={allowCustom ? "Search or type to add…" : "Search…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full text-sm py-1 focus:outline-none bg-transparent"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.map((o) => {
            const checked = o.value === value;
            return (
              <div
                key={o.value}
                onClick={() => pick(o.value)}
                className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                data-testid={`${testid}-option-${o.value}`}
              >
                <span className={cn("w-4 h-4 rounded-full border flex items-center justify-center shrink-0",
                  checked ? "bg-[#0B7E8C] border-[#0B7E8C]" : "border-slate-300")}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="truncate text-slate-900">{o.label}</span>
              </div>
            );
          })}
          {filtered.length === 0 && !allowCustom && (
            <div className="px-3 py-6 text-center text-xs text-slate-500">{emptyText}</div>
          )}
          {allowCustom && query.trim() && !exactMatch && (
            <div
              onClick={() => pick(query.trim())}
              className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-[#EAF8FA] cursor-pointer text-sm text-[#0B7E8C] border-t border-slate-100"
              data-testid={`${testid}-add-custom`}
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">Use “{query.trim()}” (Other)</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
